// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: how dashboard numbers, time buckets and periods read. The server sends raw values
// (hours as hours, ratios 0–1, bucket keys like 2026-09 or weekday 3); everything a person
// sees is formatted here, in their locale.

import {
	DASHBOARD_NONE_KEY,
	DASHBOARD_OTHER_KEY,
	type DashboardBucket,
	type DashboardCell,
	type DashboardColumn,
	type DashboardFormat,
	type DashboardPeriodPreset,
	type DashboardResolvedPeriod,
} from '@fluxer/schema/src/domains/konvo/AssistantDashboard';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';

export const OTHER_DESCRIPTOR = msg({
	message: 'Other',
	comment: 'Dashboard: the group that folds the smaller values together.',
});
export const NONE_DESCRIPTOR = msg({
	message: 'None',
	comment: 'Dashboard: the group of records without a value, e.g. tasks without an assignee.',
});
export const TOTAL_DESCRIPTOR = msg({message: 'Total', comment: 'Dashboard tooltip: the sum of a stacked bar.'});
const YES_DESCRIPTOR = msg({message: 'Yes', comment: 'Dashboard: a true value in a chart or table.'});
const NO_DESCRIPTOR = msg({message: 'No', comment: 'Dashboard: a false value in a chart or table.'});
const HOURS_DESCRIPTOR = msg({
	message: '{value} h',
	comment: 'Dashboard: a number of hours, e.g. "42.5 h". Keep the short unit.',
});
const HOURS_MINUTES_DESCRIPTOR = msg({
	message: '{hours}h {minutes}m',
	comment: 'Dashboard: an exact duration, e.g. "15h 18m". Keep the short units.',
});
const MINUTES_ONLY_DESCRIPTOR = msg({
	message: '{minutes}m',
	comment: 'Dashboard: an exact duration under an hour, e.g. "45m". Keep the short unit.',
});
const WHOLE_HOURS_DESCRIPTOR = msg({
	message: '{hours}h',
	comment: 'Dashboard: an exact duration in whole hours, e.g. "8h". Keep the short unit.',
});
const MINUTES_DESCRIPTOR = msg({
	message: '{value} min',
	comment: 'Dashboard: a number of minutes, e.g. "90 min". Keep the short unit.',
});
const QUARTER_DESCRIPTOR = msg({message: 'Q{quarter} {year}', comment: 'Dashboard: a quarter, e.g. "Q3 2026".'});
const QUARTER_SHORT_DESCRIPTOR = msg({
	message: 'Q{quarter}',
	comment: 'Dashboard: a quarter on a chart axis, e.g. "Q3".',
});
const WEEK_OF_DESCRIPTOR = msg({message: 'Week of {date}', comment: 'Dashboard: a week bucket, e.g. "Week of 7 Sep".'});

export const PERIOD_PRESET_DESCRIPTORS: Record<DashboardPeriodPreset, MessageDescriptor> = {
	today: msg({message: 'Today', comment: 'Dashboard period.'}),
	yesterday: msg({message: 'Yesterday', comment: 'Dashboard period.'}),
	this_week: msg({message: 'This week', comment: 'Dashboard period.'}),
	last_week: msg({message: 'Last week', comment: 'Dashboard period.'}),
	this_month: msg({message: 'This month', comment: 'Dashboard period.'}),
	last_month: msg({message: 'Last month', comment: 'Dashboard period.'}),
	this_quarter: msg({message: 'This quarter', comment: 'Dashboard period.'}),
	last_quarter: msg({message: 'Last quarter', comment: 'Dashboard period.'}),
	this_year: msg({message: 'This year', comment: 'Dashboard period.'}),
	last_year: msg({message: 'Last year', comment: 'Dashboard period.'}),
	last_7_days: msg({message: 'Last 7 days', comment: 'Dashboard period.'}),
	last_14_days: msg({message: 'Last 14 days', comment: 'Dashboard period.'}),
	last_30_days: msg({message: 'Last 30 days', comment: 'Dashboard period.'}),
	last_90_days: msg({message: 'Last 90 days', comment: 'Dashboard period.'}),
	last_12_months: msg({message: 'Last 12 months', comment: 'Dashboard period.'}),
	all_time: msg({message: 'All time', comment: 'Dashboard period.'}),
};

/** The presets offered in the period menu, in the order people reach for them. */
export const PERIOD_MENU: ReadonlyArray<DashboardPeriodPreset> = [
	'today',
	'this_week',
	'last_week',
	'this_month',
	'last_month',
	'last_30_days',
	'last_90_days',
	'this_quarter',
	'last_quarter',
	'this_year',
	'last_12_months',
	'all_time',
];

const numberFormats = new Map<string, Intl.NumberFormat>();

function numberFormat(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
	const key = `${locale}|${JSON.stringify(options)}`;
	let format = numberFormats.get(key);
	if (!format) {
		try {
			format = new Intl.NumberFormat(locale, options);
		} catch {
			format = new Intl.NumberFormat('en-US', {...options, currency: options.currency ? 'USD' : undefined});
		}
		numberFormats.set(key, format);
	}
	return format;
}

export interface ValueFormatOptions {
	format?: DashboardFormat;
	currency?: string;
	/** Axis ticks and tight spaces: 1.2K, 3.4M. */
	compact?: boolean;
}

/** One number as people read it: "42.5 h", "61%", "R 12,400", "1,284". */
export function formatValue(i18n: I18n, value: DashboardCell | undefined, options: ValueFormatOptions = {}): string {
	if (value === null || value === undefined || value === '') return '–';
	if (typeof value === 'boolean') return i18n._(value ? YES_DESCRIPTOR : NO_DESCRIPTOR);
	if (typeof value !== 'number') return String(value);
	const locale = i18n.locale || 'en-US';
	const compact = options.compact && Math.abs(value) >= 10_000;
	switch (options.format) {
		case 'percent':
			return numberFormat(locale, {style: 'percent', maximumFractionDigits: Math.abs(value) < 0.1 ? 1 : 0}).format(
				value,
			);
		case 'hours':
			// Time is billing data: exact hours and minutes, except on axes and in tight labels.
			if (!options.compact) return exactDuration(i18n, value * 60);
			return i18n._(HOURS_DESCRIPTOR, {
				value: numberFormat(locale, {
					maximumFractionDigits: compact ? 1 : Math.abs(value) < 10 ? 2 : 1,
					...(compact ? {notation: 'compact'} : {}),
				}).format(value),
			});
		case 'minutes':
			if (!options.compact && value >= 60) return exactDuration(i18n, value);
			return i18n._(MINUTES_DESCRIPTOR, {
				value: numberFormat(locale, {maximumFractionDigits: 0, ...(compact ? {notation: 'compact'} : {})}).format(
					value,
				),
			});
		case 'currency':
			return numberFormat(locale, {
				style: 'currency',
				currency: options.currency ?? 'ZAR',
				maximumFractionDigits: compact ? 1 : Math.abs(value) >= 1000 ? 0 : 2,
				...(compact ? {notation: 'compact'} : {}),
			}).format(value);
		case 'integer':
			return numberFormat(
				locale,
				compact ? {notation: 'compact', maximumFractionDigits: 1} : {maximumFractionDigits: 0},
			).format(value);
		default:
			return numberFormat(locale, {
				maximumFractionDigits: compact ? 1 : Math.abs(value) >= 100 ? 0 : Math.abs(value) >= 10 ? 1 : 2,
				...(compact ? {notation: 'compact'} : {}),
			}).format(value);
	}
}

function exactDuration(i18n: I18n, totalMinutes: number): string {
	const negative = totalMinutes < 0;
	const rounded = Math.round(Math.abs(totalMinutes));
	const hours = Math.floor(rounded / 60);
	const minutes = rounded % 60;
	const locale = i18n.locale || 'en-US';
	const text =
		hours === 0
			? i18n._(MINUTES_ONLY_DESCRIPTOR, {minutes})
			: minutes === 0
				? i18n._(WHOLE_HOURS_DESCRIPTOR, {hours: numberFormat(locale, {maximumFractionDigits: 0}).format(hours)})
				: i18n._(HOURS_MINUTES_DESCRIPTOR, {
						hours: numberFormat(locale, {maximumFractionDigits: 0}).format(hours),
						minutes,
					});
	return negative ? `−${text}` : text;
}

function utcDate(day: string): Date {
	return new Date(`${day}T00:00:00Z`);
}

function dateText(locale: string, date: Date, options: Intl.DateTimeFormatOptions): string {
	return date.toLocaleDateString(locale, {timeZone: 'UTC', ...options});
}

/**
 * A time bucket key as a label. `short` is for axis ticks ("7 Sep", "Mon", "Sep"),
 * the long form for tooltips and tables ("Mon 7 Sep 2026", "September 2026").
 */
export function formatBucket(i18n: I18n, key: string, bucket: DashboardBucket, short = false): string {
	const locale = i18n.locale || 'en-US';
	switch (bucket) {
		case 'day':
			return dateText(
				locale,
				utcDate(key),
				short ? {day: 'numeric', month: 'short'} : {weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'},
			);
		case 'week': {
			const date = dateText(
				locale,
				utcDate(key),
				short ? {day: 'numeric', month: 'short'} : {day: 'numeric', month: 'short', year: 'numeric'},
			);
			return short ? date : i18n._(WEEK_OF_DESCRIPTOR, {date});
		}
		case 'month':
			return dateText(locale, utcDate(`${key}-01`), short ? {month: 'short'} : {month: 'long', year: 'numeric'});
		case 'quarter': {
			const [year, quarter] = key.split('-Q');
			return short ? i18n._(QUARTER_SHORT_DESCRIPTOR, {quarter}) : i18n._(QUARTER_DESCRIPTOR, {quarter, year});
		}
		case 'year':
			return key;
		case 'weekday': {
			// 2026-09-07 was a Monday; weekday 1 is Monday.
			const date = utcDate(`2026-09-${String(6 + Number(key)).padStart(2, '0')}`);
			return dateText(locale, date, {weekday: short ? 'short' : 'long'});
		}
		case 'hour_of_day':
			return `${key}:00`;
		case 'hour': {
			const [day, hour] = key.split('T');
			return `${dateText(locale, utcDate(day!), {day: 'numeric', month: 'short'})} ${hour}:00`;
		}
	}
}

/** A dimension value as a label: buckets, Other, None, yes/no, dates. */
export function formatDimension(
	i18n: I18n,
	value: DashboardCell | undefined,
	column: DashboardColumn | undefined,
	short = false,
): string {
	if (value === DASHBOARD_OTHER_KEY) return i18n._(OTHER_DESCRIPTOR);
	if (value === DASHBOARD_NONE_KEY || value === null || value === undefined || value === '')
		return i18n._(NONE_DESCRIPTOR);
	if (typeof value === 'boolean') return i18n._(value ? YES_DESCRIPTOR : NO_DESCRIPTOR);
	if (column?.bucket && typeof value === 'string') return formatBucket(i18n, value, column.bucket, short);
	if (typeof value === 'number') return formatValue(i18n, value);
	return value;
}

/**
 * Two days as one range in the locale's own order: "12–16 Oct 2026" in en-GB, "Oct 12–16, 2026"
 * in en-US. Splicing the start's day onto the end date gave "12–Oct 16, 2026" in en-US.
 */
function dateRangeText(locale: string, start: Date, end: Date): string {
	const options: Intl.DateTimeFormatOptions = {day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC'};
	if (start.getTime() === end.getTime()) return dateText(locale, start, options);
	try {
		// Days of one month read closed up, "1–23 Aug"; Intl spaces the dash ("1 – 23 Aug").
		return new Intl.DateTimeFormat(locale, options)
			.formatRange(start, end)
			.replace(/(?<!\d)(\d{1,2})\s*–\s*(\d{1,2})(?!\d)/u, '$1–$2');
	} catch {
		return `${dateText(locale, start, options)} – ${dateText(locale, end, options)}`;
	}
}

/** "This month · 1–30 Sep" style label for the resolved period. */
export function formatPeriod(i18n: I18n, period: DashboardResolvedPeriod | null, withDates = true): string {
	if (!period) return i18n._(PERIOD_PRESET_DESCRIPTORS.all_time);
	const locale = i18n.locale || 'en-US';
	const name = period.preset ? i18n._(PERIOD_PRESET_DESCRIPTORS[period.preset]) : null;
	if (!period.start || !period.end) return name ?? i18n._(PERIOD_PRESET_DESCRIPTORS.all_time);
	const range = dateRangeText(locale, utcDate(period.start), utcDate(period.end));
	if (!name) return range;
	return withDates ? `${name} · ${range}` : name;
}

/** Relative change as a signed percentage: "+12%", "−8%". */
export function formatChange(i18n: I18n, change: number): string {
	const text = numberFormat(i18n.locale || 'en-US', {
		style: 'percent',
		maximumFractionDigits: Math.abs(change) < 0.1 ? 1 : 0,
		signDisplay: 'exceptZero',
	}).format(change);
	return text.replace('-', '−');
}

export function formatTime(i18n: I18n, iso: string): string {
	const date = new Date(iso);
	const locale = i18n.locale || 'en-US';
	const today = new Date();
	const sameDay = date.toDateString() === today.toDateString();
	const time = date.toLocaleTimeString(locale, {hour: '2-digit', minute: '2-digit'});
	return sameDay ? time : `${date.toLocaleDateString(locale, {day: 'numeric', month: 'short'})} ${time}`;
}
